<i18n src="./resources/locales.json"></i18n>
<template>
  <v-dialog v-model="isOpen" max-width="50%" scrollable>
    <v-card class="surface lighten-1">
      <v-card-title>
        <span v-once class="xtium-title">{{ $t('title') }}</span>
      </v-card-title>
      <v-divider />
      <v-card-text class="pt-8 pb-10 dialog-content">
        <v-form ref="callbackForm" v-model="validForm">
          <v-row>
            <v-col lg="6" md="12" sm="12">
              <EcsCustomInput
                ref="destinationInput"
                v-model.trim="destination"
                class="custom-input"
                type="text"
                :clearable="isClearable"
                :label="$t('destination.label')"
                :placeholder="$t('destination.placeholder')"
                :rules="[isDestinationEmpty]"
                required
                @keypress="onKeypress"
              />
            </v-col>
            <v-col lg="6" md="12" sm="12">
              <EcsCustomInput
                ref="callbackBpInput"
                v-model="businessProcessId"
                required
                class="custom-input"
                type="autocomplete"
                item-text="name"
                item-value="id"
                :clearable="isClearable"
                :items="availableBusinessProcesses"
                :label="$t('callbackBp.label')"
                :placeholder="$t('callbackBp.placeholder')"
                :rules="[v => !!v || $t('validation')]"
                @change="handleBusinessProcessChange"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col lg="6" md="12" sm="12">
              <EcsCustomInput
                ref="dateInput"
                v-model="date"
                class="custom-input"
                type="date"
                :clearable="isClearable"
                :label="$t('date.label')"
                :min="getMinDate()"
                :placeholder="$t('date.placeholder')"
              />
            </v-col>
            <v-col lg="6" md="12" sm="12">
              <EcsCustomInput
                ref="startTimeInput"
                v-model="callbackStartTime"
                class="custom-input"
                type="time"
                :format="applicationSettings.timeFormat"
                :clearable="isClearable"
                :label="$t('startTime.label')"
                :placeholder="$t('startTime.placeholder')"
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col lg="6" md="12" sm="12">
              <!-- The customer time zones comes from 0 to ahead, so the rule to have zero as valid was added. -->
              <EcsCustomInput
                ref="customerTimeZoneInput"
                v-model="customerTimeZoneId"
                class="custom-input"
                item-text="name"
                item-value="value"
                type="autocomplete"
                :clearable="isClearable"
                :items="timeZones"
                :label="$t('customerTimeZone.label')"
                :placeholder="$t('customerTimeZone.placeholder')"
                :rules="[v => !!v || v === 0 || $t('validation')]"
                required
              />
            </v-col>
            <v-col lg="6" md="12" sm="12">
              <EcsCustomInput
                ref="routingTypeInput"
                v-model="routingType"
                class="custom-input"
                type="select"
                :clearable="isClearable"
                :items="routingTypeList"
                :label="$t('routingType.label')"
                :placeholder="$t('routingType.placeholder')"
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col v-if="routingType === 1" lg="6" md="12" sm="12">
              <EcsCustomInput
                ref="callbackAgentInput"
                v-model="routingDestinationId"
                class="custom-input"
                item-text="name"
                item-value="id"
                type="autocomplete"
                :clearable="isClearable"
                :items="agents"
                :label="$t('agentSelect.label')"
                :placeholder="$t('agentSelect.placeholder')"
                :rules="[v => !!v || $t('validation')]"
                required
              />
            </v-col>
            <v-col v-if="routingType === 2" lg="6" md="12" sm="12">
              <EcsCustomInput
                ref="callbackTeamInput"
                v-model="routingDestinationId"
                class="custom-input"
                item-text="name"
                item-value="id"
                type="autocomplete"
                :clearable="isClearable"
                :items="teamList"
                :label="$t('teamSelect.label')"
                :placeholder="$t('teamSelect.placeholder')"
                :rules="[v => !!v || $t('validation')]"
                required
              />
            </v-col>
            <v-col lg="6" md="12" sm="12">
              <EcsCustomInput
                ref="remarkInput"
                v-model="remark"
                class="custom-input"
                type="text"
                :clearable="isClearable"
                :label="$t('remark.label')"
                :placeholder="$t('remark.placeholder')"
              />
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
      <v-divider />
      <v-card-actions>
        <v-spacer />
        <v-btn text @click="close">
          <span v-once>{{ $t('buttons.cancel') }}</span>
        </v-btn>
        <v-btn color="primary" depressed @click="handleSave">
          <span v-once>{{ $t('buttons.save') }}</span>
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import moment from 'moment';
import { mapGetters } from 'vuex';
import { parseMessage, sortByName } from '@/utils';
import InteractionService from '@/services/interactionService';
import BusinessProcessService from '@/services/businessProcessService';
import EcsTimeFormatter from '@/mixins/ecs-time-formatter/EcsTimeFormatter';

export default {
  name: 'EcsCrmCallbackSection',

  mixins: [EcsTimeFormatter],

  data() {
    return {
      businessProcessId: '',
      callbackStartTime: '',
      customerTimeZoneId: '',
      date: '',
      destination: '',
      isClearable: false,
      isOpen: false,
      remark: '',
      routingType: 0,
      routingDestinationId: '',
      teamList: [],
      validForm: true,
      businessProcessAssignedAgents: [],
      timeZones: []
    };
  },

  computed: {
    ...mapGetters('BUSINESS_PROCESSES', ['availableBusinessProcesses']),

    ...mapGetters('CONTACTS', ['getAgents']),

    ...mapGetters('REFERENCES', ['availableTimeZones']),

    ...mapGetters('SETTINGS', ['applicationSettings', 'agentSettings']),

    agents() {
      const allAgents = this.getAgents;
      allAgents.sort(sortByName);
      const filteredAgents = allAgents.filter(agent => this.businessProcessAssignedAgents.includes(agent.name));

      return filteredAgents;
    },

    isTwentyFourFormat() {
      return this.applicationSettings.timeFormat === 'HH:mm';
    },

    routingTypeList() {
      const list = [
        { text: this.$t('routingTypeList.none'), value: 0 },
        { text: this.$t('routingTypeList.agent'), value: 1 }
      ];
      if (this.teamList.length > 0) {
        list.push({ text: this.$t('routingTypeList.team'), value: 2 });
      }
      return list;
    }
  },
  sockets: {
    AGENT_BP_ASSIGNMENT_CHANGED_EVENT() {
      if (this.businessProcessId) {
        this.getAgentsForBusinessProcess(this.businessProcessId);
      }
    }
  },
  watch: {
    async businessProcessId(newBusinessProcessId) {
      if (!newBusinessProcessId) return;

      try {
        const bpDetails = await BusinessProcessService.getBusinessProcessInformation(newBusinessProcessId);
        if (bpDetails.status !== 200 || !bpDetails.data) return;

        if (bpDetails.data.teams && bpDetails.data.teams.length) {
          this.teamList = bpDetails.data.teams;
        } else {
          this.teamList = [];
          if (this.routingType === 2) {
            // If teams doesn't exists for BP
            // and routing type was left as Team
            // the routing type becomes 0 by default
            this.routingType = 0;
          }
        }
      } catch (error) {
        console.error('Error getting the business process details while scheduling a callback.', error);
        this.teamList = [];
        this.routingType = 0;
      }
    }
  },

  created() {
    this.$root.$on('open-new-callback-dialog', this.open);
  },
  beforeDestroy() {
    this.$root.$off('open-new-callback-dialog', this.open);
  },
  methods: {
    open(interaction) {
      this.initialize();

      if (interaction) {
        if (interaction.routing) {
          this.destination = interaction.receptor.endPoint;
          this.businessProcessId = interaction.routing.businessProcessId;
        }
        this.date = this.getCurrentDate();
        this.callbackStartTime = this.getHourAfterNow();
        this.routingType = 0;
      } else {
        this.businessProcessId = this.agentSettings.defaultOutgoingCallBusinessProcessId;
      }

      if (this.businessProcessId) {
        this.getAgentsForBusinessProcess(this.businessProcessId);
      }

      this.isOpen = true;
    },

    close() {
      this.isOpen = false;
    },

    generateCallbackPayload() {
      // Always convert time to 24 format for the service
      const formattedTime = moment(this.callbackStartTime, this.applicationSettings.timeFormat).format('HH:mm');

      const payload = {
        bpId: this.businessProcessId,
        callbackDestination: this.destination,
        startTime: `${this.date} ${formattedTime}`,
        routingType: this.routingType
      };

      // Note: This is the only way we can do 'optional' fields via ECS API,
      // they must have a value or not be present at all
      if (this.routingType !== 0 && this.routingDestinationId) {
        payload.routingDestinationId = this.routingDestinationId;
      } else {
        delete payload.routingDestinationId;
      }

      // The customer time zones comes from 0 to ahead, so the validation to have zero as valid was added.
      if (!!this.customerTimeZoneId || this.customerTimeZoneId === 0) {
        payload.customerTimeZoneId = this.customerTimeZoneId;
      }

      if (this.remark) {
        payload.remark = parseMessage(this.remark);
      }

      return payload;
    },

    getCurrentDate() {
      return moment().format('YYYY-MM-DD');
    },

    getHourAfterNow() {
      return this.getCurrentTime().add(1, 'hour');
    },

    getMinDate() {
      const date = new Date();
      return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().split('T')[0];
    },

    handleSave() {
      if (!this.validate()) {
        return;
      }

      InteractionService.scheduleCallback(this.generateCallbackPayload())
        .then(result => {
          this.$root.$emit('snackbar-message', {
            message: this.$t('newCallbackRequest'),
            type: 'success'
          });
        })
        .finally(this.close);
    },

    initialize() {
      // Set default form values
      this.destination = '';
      this.businessProcessId = '';
      this.date = this.getCurrentDate();
      this.callbackStartTime = this.getHourAfterNow();
      this.customerTimeZoneId = this.applicationSettings.timeZoneId;
      this.routingType = 0;
      this.routingDestinationId = '';
      this.remark = '';
      this.timeZones = this.availableTimeZones;

      // Clear 'invalid' states
      if (this.$refs.callbackForm) {
        Object.values(this.$refs).forEach(i => {
          if (i && typeof i.validate === 'function') {
            i.valid = true;
          }
        });
        this.$refs.callbackForm.resetValidation();
      }
    },

    onKeypress(event) {
      const { keyCode } = event;
      if (keyCode > 31 && (keyCode < 48 || keyCode > 57)) {
        event.preventDefault();
      }
    },

    validate() {
      this.$refs.callbackForm.validate();

      let valid = true;
      Object.values(this.$refs).forEach(input => {
        if (input && typeof input.validate === 'function') {
          if (!input.validate()) {
            valid = false;
          }
        }
      });

      return valid;
    },

    isDestinationEmpty(value) {
      return value !== '' || this.$t('validation');
    },

    handleBusinessProcessChange(value) {
      if (this.routingType === 1) {
        this.routingDestinationId = '';
      }
      this.getAgentsForBusinessProcess(value);
    },

    async getAgentsForBusinessProcess(businessProcessId) {
      const assignedAgents = await BusinessProcessService.getBusinessProcessInformation(businessProcessId);
      this.businessProcessAssignedAgents = assignedAgents.data.assignedAgents;
    }
  }
};
</script>

<style scoped>
.custom-input:not(:last-child) {
  margin-bottom: 16px;
}

.success-snackbar {
  position: absolute;
  top: 0;
}

.dialog-content {
  background-color: var(--v-surface-base);
}
</style>
